/*eslint-disable @typescript-eslint/no-unsafe-argument*/
/*eslint-disable @typescript-eslint/restrict-template-expressions*/
import React, { useContext, useEffect, useState } from "react";

import styled from "@emotion/styled";

import { DataContext } from "../../../../../providers/ElectricityDataProvider";
import pushToAnalytics from "../../../../../utils/analytics";
import NewInput from "../../../../shared/forms/NewInput";
import NewSelectInput from "../../../../shared/forms/NewSelectInput";
import Button from "../../../../shared/NewForms/Button";
import ConsumptionInput from "./ConsumptionInput";
import ZipcodeInput from "./ZipcodeInput";

const Form = styled.form`
    display: flex;
    flex-flow: row wrap;
    justify-content: flex-start;
    width: 100%;
    margin-bottom: 20px;
    gap: 6px 12px;

    ${({ theme }) => theme.breakpoints.from.small.css`
        column-gap: 5%;
        justify-content: start;
    `}
`;

const ButtonWrapper = styled.div`
    display: flex;
    justify-content: center;
    width: 100%;
    margin-top: 20px;
    margin-bottom: 30px;
`;

const InputWrapper = styled.div`
    display: flex;
    flex-direction: column;
    width: 48%;

    ${({ theme }) => theme.breakpoints.from.small.css`
        width: 30%;
    `}
`;

const Calculator = () => {
    const DISABLE_RESET_VALUE_ON_RELOAD = 0;
    const ENABLE_RESET_VALUE_ON_RELOAD = 1;
    const dataContext = useContext(DataContext);
    const {
        zipcode,
        consumption,
        tariffType,
        validResult,
        validZipcode,
        streetSelectList,
        streetSelectListSelected,
        placeSelectList,
        placeSelectListSelected,
        houseNumber,
        maxAmount,
        maxConsumptionError,
    } = dataContext.data;

    const {
        setZipcode,
        setConsumption,
        setTariffId,
        setVerbandsnummer,
        setTariffType,
        calculate,
        setStreetSelectList,
        setStreetSelectListSelected,
        setPlaceSelectList,
        setPlaceSelectListSelected,
        setValidZipcode,
        setValidResult,
        setShowPlaceInput,
        setShowStreetInput,
        setShowHouseNumberInput,
        setHouseNumber,
        getPlacesSelect,
        getStreetsSelect,
        setFormErrors,
        clearFormError,
        setMaxConsumptionError,
    } = dataContext.actions;

    const [, setZipcodeError] = useState(false);
    const [, setHouseNumberError] = useState(false);
    const [localZipcode, setLocalZipcode] = useState(zipcode);
    const [, setLocalPlace] = useState("");
    const [localConsumption, setLocalConsumption] = useState(consumption);
    const [localTariffType] = useState(tariffType);
    const [calculatorInputValid, setCalculatorInputValid] = useState(false);
    const [, setCalculateClicked] = useState(false);

    useEffect(() => {
        setVerbandsnummer("");
        setTariffId("");

        // get places on initial load if zipcode exists
        if (localZipcode.length === 5) {
            getPlacesSelect(localZipcode, DISABLE_RESET_VALUE_ON_RELOAD);
        }
        // get street list on initial load if zipcode and place is set
        if (
            localZipcode.length === 5 &&
            placeSelectListSelected[0].value !== "Bitte Wählen"
        ) {
            getStreetsSelect(
                localZipcode,
                placeSelectListSelected[0].value,
                "",
                DISABLE_RESET_VALUE_ON_RELOAD
            );
        }

        //Only submit if all inputs are set and the result is missing, otherwise it will submit every time the form gets
        //mounted (eg. Eingaben ändern Button) which closes the form again after the result is set.
        if (
            validZipcode &&
            placeSelectListSelected &&
            streetSelectListSelected &&
            houseNumber &&
            !validResult
        ) {
            onSubmit();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const selectsShowAndReset = (hasChanged) => {
        setFormErrors([]);
        if (hasChanged === "zipcode") {
            setShowPlaceInput(false);
            setShowStreetInput(false);
            setShowHouseNumberInput(false);
            //cleanup
            setPlaceSelectListSelected([{ value: "", label: "Ort" }]);
            setPlaceSelectList([{ value: "", label: "" }]);
            setStreetSelectListSelected([{ value: "", label: "Straße" }]);
            setStreetSelectList([{ value: "", label: "" }]);
            setHouseNumber("");
        } else if (hasChanged === "place") {
            setShowStreetInput(false);
            setShowHouseNumberInput(false);
            //cleanup
            setStreetSelectListSelected([{ value: "", label: "Bitte wählen" }]);
            setStreetSelectList([{ value: "", label: "" }]);
            setHouseNumber("");
        } else if (hasChanged === "street") {
            setShowHouseNumberInput(false);
            setHouseNumber("");
        }
    };

    const onChangeZipcode = (zipcode) => {
        selectsShowAndReset("zipcode");
        if (!isNaN(zipcode)) {
            // only numbers as input shown
            setLocalZipcode(zipcode);
            // calculate only if input valid
            if (zipcode.length === 5 && typeof localZipcode == "string") {
                setCalculatorInputValid(true);
                setValidZipcode(true);
                setZipcode(zipcode);
                getPlacesSelect(zipcode, ENABLE_RESET_VALUE_ON_RELOAD);
            }
        }
    };

    const onChangePlace = (e) => {
        selectsShowAndReset("place");
        setLocalPlace(e.value);
        setPlaceSelectListSelected([
            {
                label: e.label,
                value: e.value,
                value2: {
                    place: e.value2.ortsname,
                    placePart: e.value2.ortsteil,
                },
            },
        ]);
        setCalculatorInputValid(true);
        getStreetsSelect(
            localZipcode,
            e.value2.ortsname,
            e.value2.ortsteil,
            ENABLE_RESET_VALUE_ON_RELOAD
        );
    };

    const onChangeStreet = (e) => {
        selectsShowAndReset("street");
        setStreetSelectListSelected([
            { value: `${e.value}`, label: `${e.value}` },
        ]);
        setCalculatorInputValid(true);
    };

    const onChangeHouseNumber = (e) => {
        selectsShowAndReset("houseNumber");
        setHouseNumber(e.target.value);
        clearFormError("housenumber");
        setCalculatorInputValid(true);
    };

    const onChangeConsumptionInput = (e) => {
        e.preventDefault();
        // allow only numbers
        if (!isNaN(e.target.value)) {
            setLocalConsumption(e.target.value.toString());

            if (e.target.value > maxAmount) {
                setMaxConsumptionError(true);
                setCalculatorInputValid(false);
                // setLocalConsumption(maxAmount);
            } else {
                setMaxConsumptionError(false);
                setCalculatorInputValid(true);
            }
        }
        setCalculatorInputValid(true);
    };

    const onSubmit = (e) => {
        if (e) {
            e.preventDefault();
        }

        let localPlace = "";
        let localPlacePart = "";
        let localStreet = "";
        setCalculateClicked(true);

        if (
            placeSelectListSelected[0].value2 &&
            placeSelectListSelected[0].value2.place
        ) {
            localPlace = placeSelectListSelected[0].value2.place;
        }
        if (
            placeSelectListSelected[0].value2 &&
            placeSelectListSelected[0].value2.placePart
        ) {
            localPlacePart = placeSelectListSelected[0].value2.placePart;
        }
        if (streetSelectListSelected[0].value) {
            localStreet = streetSelectListSelected[0].value;
        }

        if (houseNumber.length > 1) {
            setHouseNumber(houseNumber);
        }
        setConsumption(localConsumption);
        setTariffType(localTariffType);

        if (
            localZipcode.length === 5 &&
            !isNaN(localZipcode) &&
            !maxConsumptionError
        ) {
            // Trigger a gtm event
            // If we don't have an event, there was no user interaction and we
            // don't want to trigger a google tag manager event.
            // This function gets called, when the user has valid data in the
            // local storage and enters this site.
            if (e) {
                pushToAnalytics("ele:calculate", {
                    zipcode: localZipcode,
                    consumption: localConsumption,
                });
            }
            calculate(
                localZipcode,
                localConsumption,
                localPlace,
                localPlacePart,
                localStreet,
                houseNumber,
                localTariffType
            );
            setCalculatorInputValid(false);
        } else {
            setValidZipcode(false);
            setValidResult(false);
        }
    };

    return (
        <Form onSubmit={onSubmit}>
            <InputWrapper>
                <ZipcodeInput
                    setZipcode={onChangeZipcode}
                    localZipcode={localZipcode}
                    zipcodeError={
                        !zipcode &&
                        !validResult &&
                        streetSelectList.constructor === Object &&
                        Object.keys(streetSelectList).length <= 1
                    }
                    setZipcodeError={setZipcodeError}
                />
            </InputWrapper>
            <InputWrapper>
                <ConsumptionInput
                    localConsumption={localConsumption}
                    onChangeConsumption={onChangeConsumptionInput}
                />
            </InputWrapper>
            <InputWrapper>
                <NewSelectInput
                    name="place"
                    placeholder="Ort"
                    value={placeSelectListSelected}
                    onChange={onChangePlace}
                    errorClassName="is-light"
                    options={
                        Array.isArray(placeSelectList) &&
                        placeSelectList[0].value !== "Bitte wählen"
                            ? placeSelectList
                            : []
                    }
                    noOptionsMessage={() => "Keinen Ort gefunden."}
                    isDisabled={
                        !(
                            Array.isArray(placeSelectList) &&
                            placeSelectList[0].value !== "Bitte wählen"
                        )
                    }
                />
            </InputWrapper>
            <InputWrapper>
                <NewSelectInput
                    name="street"
                    placeholder="Straße"
                    value={streetSelectListSelected}
                    onChange={onChangeStreet}
                    errorClassName="is-light"
                    options={
                        Array.isArray(streetSelectList) &&
                        streetSelectList[0].value !== "Bitte wählen"
                            ? streetSelectList
                            : []
                    }
                    isDisabled={!placeSelectListSelected}
                    noOptionsMessage={() => "Keine Straße gefunden."}
                    theme={(theme) => {
                        return {
                            ...theme,
                            borderRadius: "6px",
                            colors: {
                                ...theme.colors,
                                text: "black",
                            },
                        };
                    }}
                    withoutWrapper
                />
            </InputWrapper>
            <InputWrapper>
                <NewInput
                    name="houseNumber"
                    placeholder="Hausnummer"
                    value={houseNumber}
                    onChange={onChangeHouseNumber}
                    onError={() => setHouseNumberError(true)}
                    onErrorSolved={() => setHouseNumberError(false)}
                    required
                />
            </InputWrapper>
            <ButtonWrapper>
                <Button
                    type="submit"
                    data-gtag="ele-calculate-advanced"
                    color={calculatorInputValid ? "red" : "redOutline"}
                >
                    Neu berechnen
                </Button>
            </ButtonWrapper>
        </Form>
    );
};

export default Calculator;
